import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { Trash } from "lucide-react";
import { DropzoneRootProps, DropzoneInputProps } from "react-dropzone";

interface FileDropzoneProps {
  getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  isDragActive: boolean;
  selectedFiles: File[];
  onRemoveFile: (index: number) => void;
  textConfig?: Record<string, string>;
  isDarkMode?: boolean;
  disabled?: boolean;
  showFileList?: boolean;
  className?: string;
  style?: React.CSSProperties;
  truncateFileName?: (fileName: string, maxLength?: number) => string;
}

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  getRootProps,
  getInputProps,
  isDragActive,
  selectedFiles,
  onRemoveFile,
  textConfig: externalTextConfig,
  isDarkMode = true,
  disabled = false,
  showFileList = true,
  className = "",
  style = {},
  truncateFileName = (name) => name,
}) => {
  
  const [internalTextConfig, setInternalTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setInternalTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  // Use external textConfig if provided, otherwise use internal
  const textConfig = externalTextConfig || internalTextConfig;
  
  const defaultStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? "#27272a" : "#f9fafb",
    marginBottom: "1rem",
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
    ...style
  };

  const borderColor = isDarkMode ? "border-gray-600" : "border-gray-300";
  const activeStyles = isDragActive ? "border-green-400 bg-green-50 dark:bg-green-900/20" : "";
  
  return (
    <>
      <div
        {...getRootProps()}
        className={`border-dashed border-2 p-4 rounded-lg cursor-pointer transition-colors ${borderColor} ${activeStyles} ${className}`}
        style={defaultStyle}
      >
        <input {...getInputProps()} disabled={disabled} />
        {isDragActive ? (
          <p className="text-green-400 text-center">
            {textConfig?.FILE_DROPZONE_TEXT_DROP_HERE}
          </p>
        ) : (
          <p className={`text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            {textConfig?.FILE_DROPZONE_TEXT_DRAG_OR_CLICK}
          </p>
        )}
      </div>

      {showFileList && selectedFiles.length > 0 && (
        <Box 
          mt={2} 
          p={2} 
          bgcolor={isDarkMode ? "#27272a" : "#f3f4f6"} 
          borderRadius={2}
        >
          <Typography 
            color={isDarkMode ? "#fff" : "#000"} 
            variant="body2" 
            mb={2}
          >
            {textConfig?.FILE_DROPZONE_TEXT_SELECTED_FILES}
          </Typography>
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {selectedFiles.map((file, index) => (
              <li 
                key={index} 
                className={`flex justify-between items-center mb-1 ${
                  isDarkMode ? "text-white" : "text-gray-800"
                } ${file.name.length > 150 ? "text-red-500" : ""}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "4px",
                  padding: "4px 0"
                }}
              >
                <span style={{ flex: 1 }}>
                  {truncateFileName(file.name)} - {(file.size / 1024).toFixed(2)} {textConfig?.FILE_DROPZONE_TEXT_FILE_SIZE_KB}
                  {file.name.length > 150 && (
                    <span style={{ color: "#FF3D3D", marginLeft: "4px" }}>
                      ({textConfig?.FILE_DROPZONE_NAME_TOO_LONG})
                    </span>
                  )}
                </span>
                <Trash
                  size={16}
                  className="text-red-500 cursor-pointer hover:text-red-400"
                  style={{ 
                    color: "#EF4444",
                    cursor: "pointer",
                    marginLeft: "8px",
                    flexShrink: 0
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemoveFile(index);
                  }}
                />
              </li>
            ))}
          </ul>
        </Box>
      )}
    </>
  );
};

export default FileDropzone;